import { v4 } from 'uuid';
import type { StateCreator } from 'zustand/vanilla';
import { AGENT_SCENE_SETTINGS_CONFIG, DEFAULT_SCENE_ID } from '@/app/constants';
import type {
  AgentChatInput,
  AgentMediaItem,
  AgentReferenceContent,
  AgentSceneConfig,
  AgentSceneType,
  AgentSettings,
  AgentVoiceOption,
  TextPromptWithInputItem,
} from '@/app/types';

/**
 * 每个 tab 的完整运行时状态
 */
export interface TabRuntimeState {
  /** Tab 基础信息 */
  id: string;
  sceneId: AgentSceneType;
  /** 关联的聊天 ID */
  chatId?: string;
  voice?: AgentVoiceOption;
  settingsConfig?: AgentSceneConfig['settingsConfig'];
  /** 输入状态 */
  inputValue: TextPromptWithInputItem;
  /** 引用状态 */
  reference: AgentReferenceContent | undefined;
  /** 上传的媒体文件 */
  uploadList: AgentMediaItem[];
  /** 设置状态 */
  selectedSettings: AgentSettings;
  /** 发送状态 */
  sending: boolean;
}

interface AgentChatTabsState {
  /** 所有打开的 tabs 的完整运行时状态 */
  agentChatTabs: TabRuntimeState[];
  /** 当前激活的 tab ID */
  activeAgentChatTabId: string | undefined;
  /** 窗口是否展开 */
  isAgentChatTabExpanded: boolean;
  /** 临时 tab 信息 */
  tempTabInfo: AgentChatInput | null;
}

/**
 * 创建默认的 tab 状态
 *
 * @param id - Tab ID
 * @param title - Tab 标题
 * @param sceneId - 场景 ID
 * @param chatId - 关联的聊天 ID
 * @returns 默认的 tab 运行时状态
 */
function createDefaultTabState({
  id,
  sceneId,
  chatId,
  voice,
  selectedSettings,
}: {
  id: string;
  sceneId: AgentSceneType;
  chatId?: string;
  voice?: AgentVoiceOption;
  selectedSettings?: AgentSettings;
}): TabRuntimeState {
  const settingsConfig = AGENT_SCENE_SETTINGS_CONFIG[sceneId];
  const { duration, resolution, ratio } = settingsConfig || {};
  const defaultSettings = {
    ...(duration && { duration: duration[0] }),
    ...(resolution && { resolution: resolution[0] }),
    ...(ratio && { ratio: ratio[0] }),
  };
  return {
    id,
    sceneId,
    chatId,
    settingsConfig: settingsConfig,
    inputValue: [],
    reference: undefined,
    uploadList: [],
    selectedSettings: selectedSettings ? selectedSettings : { ...defaultSettings },
    sending: false,
    voice,
  };
}

export interface AgentChatTabsStore extends AgentChatTabsState {
  /** 添加新 tab */
  addAgentChatTab: (tabInfo: Partial<TabRuntimeState>) => void;
  /** 切换 tab */
  changeAgentChatTab: (tabId: string) => void;
  /** 批量添加 tab, 并激活最后一个 tab */
  batchAddAgentChatTabs: (
    list: { chatId: string; sceneId: AgentSceneType; voice?: AgentVoiceOption; settings?: AgentSettings }[],
  ) => void;
  /** 更新指定 tab 的状态 */
  updateAgentChatTab: (
    tabId: string | undefined,
    updates: Partial<TabRuntimeState> | ((prevTab: TabRuntimeState | undefined) => Partial<TabRuntimeState>),
  ) => void;
  /** 设置窗口展开状态 */
  setIsAgentChatTabExpanded: (isAgentChatTabExpanded: boolean) => void;
  /** 重置所有状态到初始值 */
  resetAgentChatTabs: () => void;
  /** 设置临时 tab 信息 */
  setTempTabInfo: (tempTabInfo: AgentChatInput | null) => void;
}

const initialState: AgentChatTabsState = {
  agentChatTabs: [],
  activeAgentChatTabId: undefined,
  isAgentChatTabExpanded: false,
  tempTabInfo: null,
};

export const createAgentChatTabsSlice: StateCreator<AgentChatTabsStore, [], [], AgentChatTabsStore> = (
  set,
  get,
  _store,
) => ({
  ...initialState,

  /** 添加新 tab */
  addAgentChatTab: (tabInfo: Partial<TabRuntimeState>) => {
    // 创建新 tab
    const newTab = createDefaultTabState({
      ...tabInfo,
      id: tabInfo.id || v4(),
      sceneId: tabInfo.sceneId || DEFAULT_SCENE_ID,
      chatId: tabInfo.chatId,
    });
    set((state) => ({ agentChatTabs: state.agentChatTabs.concat(newTab), activeAgentChatTabId: newTab.id }));
  },
  /** 切换 tab */
  changeAgentChatTab: (tabId: string) => {
    const { agentChatTabs } = get();
    if (agentChatTabs.some((t) => t.id === tabId)) {
      set({ activeAgentChatTabId: tabId });
    }
  },

  /** 更新 tab 的 settings */
  batchAddAgentChatTabs: (
    list: { chatId: string; sceneId: AgentSceneType; voice?: AgentVoiceOption; settings?: AgentSettings }[],
  ) => {
    const newTabs = list.map((item) => {
      const newTab = createDefaultTabState({
        id: v4(),
        sceneId: item.sceneId,
        chatId: item.chatId,
        voice: item.voice,
        selectedSettings: item.settings,
      });
      return newTab;
    });
    set((state) => ({
      agentChatTabs: state.agentChatTabs.concat(newTabs),
      activeAgentChatTabId: newTabs[newTabs.length - 1].id,
      isAgentChatTabExpanded: true,
    }));
  },
  /** 更新指定 tab 的状态 */
  updateAgentChatTab: (
    tabId: string | undefined,
    updates: Partial<TabRuntimeState> | ((prevTab: TabRuntimeState | undefined) => Partial<TabRuntimeState>),
  ) => {
    if (!tabId) return;
    set((state) => {
      const prevTab = state.agentChatTabs.find((tab) => tab.id === tabId);
      const updateData = typeof updates === 'function' ? updates(prevTab) : updates;
      return {
        agentChatTabs: state.agentChatTabs.map((tab) => (tab.id === tabId ? { ...tab, ...updateData } : tab)),
      };
    });
  },
  /** 设置窗口展开状态 */
  setIsAgentChatTabExpanded: (isAgentChatTabExpanded: boolean) => {
    set({ isAgentChatTabExpanded });
  },
  /** 重置所有状态到初始值, 保留临时 tab 信息 */
  resetAgentChatTabs: () => {
    set((state) => ({ ...initialState, tempTabInfo: state.tempTabInfo }));
  },
  /** 设置临时 tab 信息 */
  setTempTabInfo: (tempTabInfo: AgentChatInput | null) => {
    set({ tempTabInfo });
  },
});
